import { baseApi } from './baseApi';
import { parseVersionCheckResponse } from 'store/parsers/common.parsers';

/**
 * Common API Slice
 * Common endpoints including version checking and app configuration
 */
export const commonApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Check for app updates
     * @param {Object} params - Version check parameters
     * @param {string} params.platform - Platform ('android', 'ios', 'web')
     * @param {string} params.currentVersion - Current app version
     * @returns {Object} Update information
     */
    checkVersion: builder.query({
      query: ({ platform, currentVersion, versionCode }) => ({
        url: '/version/check',
        method: 'GET',
        params: {
          platform,
          currentVersion,
          versionCode
        }
      }),
      transformResponse: (response) => parseVersionCheckResponse(response),
      providesTags: ['Version']
    }),
    
    /**
     * Get app configuration
     * @returns {Object} App configuration
     */
    getAppConfig: builder.query({
      query: () => ({
        url: '/app/config',
        method: 'GET'
      }),
      providesTags: ['AppConfig']
    }),
    
    /**
     * Get feature configuration
     * @returns {Object} Feature configuration
     */
    getFeatureConfig: builder.query({
      query: () => ({
        url: '/api/common/feature-config',
        method: 'GET'
      }),
      providesTags: ['FeatureConfig']
    }),

    /**
     * Get Privacy Policy
     * @returns {string} Privacy Policy HTML content
     */
    getPrivacyPolicy: builder.query({
      query: () => ({
        url: '/privacy-policy',
        method: 'GET',
        responseHandler: (response) => response.text(),
      }),
      providesTags: ['PrivacyPolicy']
    }),

    /**
     * Get app update configuration
     * @param {string} osType - OS Type ('ANDROID', 'IOS')
     * @returns {Object} App update configuration
     */
    getAppUpdateConfig: builder.query({
      query: (osType) => ({
        url: '/api/force-update/config',
        method: 'GET',
        params: { osType }
      }),
      providesTags: ['AppUpdateConfig']
    }),

    /**
     * Get Terms and Conditions
     * @returns {string} Terms and Conditions HTML content
     */
    getTermsConditions: builder.query({
      query: () => ({
        url: '/terms-conditions',
        method: 'GET',
        responseHandler: (response) => response.text(),
      }),
      providesTags: ['TermsConditions']
    }),
  })
});

// Export hooks for use in components
export const {
  useCheckVersionQuery,
  useLazyCheckVersionQuery,
  useGetAppConfigQuery,
  useGetFeatureConfigQuery,
  useGetPrivacyPolicyQuery,
  useGetTermsConditionsQuery,
  useGetAppUpdateConfigQuery,
  useLazyGetAppUpdateConfigQuery
} = commonApi;
